import { AsyncStore, defaultRandomId } from "./async.storage";
import { ErrorsOr, flatMapErrorsOrK, isErrors, mapErrorsOr } from "@me8eon/errors";
import { dateTimeService, TimeService } from "@me8eon/time";

/**
 * A parent along with an optional newly generated child.
 * Returned when a child has been deleted and possibly replaced.
 */
export type ParentAndOptionalChild<Parent, ChildId, Child> = {
    parent: Parent
    childAndId?: NewChildResult<ChildId, Child>
}

/**
 * Injection object containing a random ID generator and time service.
 * Used for creating new parents/children.
 */
export type RandomIdAndTimeService = {
    random: () => string
    timeService: TimeService
}

/**
 * Configuration required to wire together parent/child storage logic.
 */
export type ParentChildAsyncStorageConfig<ParentId, Parent, ChildId, Child> = {
    parentStore: AsyncStore<ParentId, Parent>
    childStore: AsyncStore<ChildId, Child>

    /** Extracts the child ID from the given parent. */
    childId: (p: Parent) => ChildId

    /**
     * Creates a new child when creating a new parent.
     */
    newChildForNewParent: (parent: Parent, parentId: ParentId) => NewChildResult<ChildId, Child>

    /**
     * After deleting one or more children, this produces a new version of the parent,
     * and possibly a new child to accompany it.
     */
    makeNewParentWithDeletedChildIds: (parent: Parent, childIds: ChildId[], config: RandomIdAndTimeService) => ParentAndOptionalChild<Parent, ChildId, Child>
}

/**
 * Represents the result of creating a new child.
 */
export type NewChildResult<ChildId, Child> = {
    childId: ChildId
    child: Child
}

/**
 * A full representation of a parent and its child, along with their IDs.
 */
export type ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child> = {
    parentId: ParentId
    parent: Parent
    childId: ChildId
    child: Child
}

/**
 * A parent and its child, without IDs.
 */
export type ParentAndChild<Parent, Child> = {
    parent: Parent
    child: Child
}

/**
 * A parent and its corresponding ID.
 */
export type ParentIdAndParent<ParentId, Parent> = {
    parentId: ParentId
    parent: Parent
}

/**
 * Interface for working with parents and their associated children in persistent storage.
 */
export type ParentAsyncStorage<ParentId, Parent, ChildId, Child> = {
    /** Stores both the parent and child in the backing stores. */
    storeParentWithChild: (parentAndChild: ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>) => Promise<ErrorsOr<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>>

    /** Loads both the parent and its associated child by parent ID. */
    getParentAndChild: (id: ParentId, undefinedIfNotIn?: boolean) => Promise<ErrorsOr<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>>

    /** Retrieves the parent only by ID. May return undefined if requested and not found. */
    getParent: (id: ParentId, undefinedIfNotIn?: boolean) => Promise<ErrorsOr<Parent>>

    /** Stores only the parent, without touching the child store. */
    storeJustParent: (parent: Parent, id: ParentId) => Promise<ErrorsOr<void>>

    /** Deletes parents and their associated children. */
    deleteParentAndChildren: (...parents: ParentIdAndParent<ParentId, Parent>[]) => Promise<ErrorsOr<void>>

    /** Retrieves the child only by ID. May return undefined if requested and not found. */
    getChild: (id: ChildId, undefinedIfNotIn?: boolean) => Promise<ErrorsOr<Child>>

    /** Stores only the child, without touching the parent store. */
    storeJustChild: (child: Child, id: ChildId) => Promise<ErrorsOr<void>>

    /**
     * Deletes one or more children, then updates the parent.
     * May also generate and return a new child if needed.
     */
    deleteChildAndUpdateParentAndMaybeNewChild: (
        parent: Parent,
        id: ParentId,
        ids: ChildId[],
        injections?: RandomIdAndTimeService,
    ) => Promise<ErrorsOr<ParentAndOptionalChild<Parent, ChildId, Child>>>

    /** Deletes a parent by ID, without touching the child store. */
    deleteParent: (id: ParentId) => Promise<ErrorsOr<void>>
}

/**
 * Stores both parent and child in their respective stores.
 */
export function storeParentWithChild<ParentId, Parent, ChildId, Child>(
    config: ParentChildAsyncStorageConfig<ParentId, Parent, ChildId, Child>,
) {
    return async (
        parentWithChild: ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>,
    ): Promise<ErrorsOr<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>> => {
        const { parentId, parent, childId, child } = parentWithChild;
        if (parentId === undefined) throw new Error(`Parent id is undefined. ${parentWithChild}`);

        const storeChildResult = await config.childStore.store(childId, child);
        if (isErrors(storeChildResult)) return storeChildResult;

        const storeParentResult = await config.parentStore.store(parentId, parent);
        if (isErrors(storeParentResult)) return storeParentResult;
        return { value: parentWithChild };
    };
}

/**
 * Loads a parent and its child using the parent ID.
 * If `undefinedIfNotIn` is true, missing entries return `value: undefined`.
 */
export const getParentAndChild = <ParentId, Parent, ChildId, Child>(
    config: ParentChildAsyncStorageConfig<ParentId, Parent, ChildId, Child>,
) => async (
    id: ParentId,
    undefinedIfNotIn?: boolean,
): Promise<ErrorsOr<ParentAndChildrenWithIds<ParentId, Parent, ChildId, Child>>> => {
    const parentResult = await config.parentStore.get(id, undefinedIfNotIn);
    if (isErrors(parentResult)) return parentResult;
    if (undefinedIfNotIn && !parentResult.value) return { value: undefined as any };

    const childId = config.childId(parentResult.value);
    const childResult = await config.childStore.get(childId, undefinedIfNotIn);
    if (isErrors(childResult)) return childResult;

    return {
        value: { parentId: id, parent: parentResult.value, childId, child: childResult.value },
    };
};

/**
 * Deletes one or more parent-child pairs.
 * Note: if child deletion fails, no rollback is attempted.
 */
export const deleteParentAndChildren = <ParentId, Parent, ChildId, Child>(
    config: ParentChildAsyncStorageConfig<ParentId, Parent, ChildId, Child>,
) => async (
    ...parents: ParentIdAndParent<ParentId, Parent>[]
): Promise<ErrorsOr<void>> => {
    const parentIds = parents.map(p => p.parentId);
    const deleteParentsResult = await config.parentStore.delete(...parentIds);
    if (isErrors(deleteParentsResult)) return deleteParentsResult;

    const childIds = parents.map(p => config.childId(p.parent));
    const deleteChildrenResult = await config.childStore.delete(...childIds);

    // Compensation for failed child deletions is intentionally omitted.
    return deleteChildrenResult;
};

/**
 * Creates an instance of `ParentAsyncStorage` with full access to parent-child storage operations.
 */
export function makeParentAsyncStorage<ParentId, Parent, ChildId, Child>(
    config: ParentChildAsyncStorageConfig<ParentId, Parent, ChildId, Child>,
): ParentAsyncStorage<ParentId, Parent, ChildId, Child> {
    return {
        storeParentWithChild: storeParentWithChild(config),
        getParentAndChild: getParentAndChild(config),
        getParent: (id, undefinedIfNotIn) => config.parentStore.get(id, undefinedIfNotIn),
        storeJustParent: (parent, id) => {
            if (id === undefined) throw new Error(`Parent id is undefined in store parent ${parent}`);
            return config.parentStore.store(id, parent);
        },
        deleteParent: (p) => config.parentStore.delete(p),
        deleteParentAndChildren: deleteParentAndChildren(config),
        getChild: (id, undefinedIfNotIn) => config.childStore.get(id, undefinedIfNotIn),
        storeJustChild: (child, id) => {
            if (id === undefined) throw new Error(`Child id is undefined in store child ${child}`);
            return config.childStore.store(id, child);
        },
        deleteChildAndUpdateParentAndMaybeNewChild: async (parent: Parent, parentId, ids, injections) =>
            flatMapErrorsOrK(await config.childStore.delete(...ids), async () => {
                const { parent: newParent, childAndId } =
                    config.makeNewParentWithDeletedChildIds(
                        parent,
                        ids,
                        injections || { random: defaultRandomId, timeService: dateTimeService },
                    );

                return flatMapErrorsOrK<void, ParentAndOptionalChild<Parent, ChildId, Child>>(
                    await config.parentStore.store(parentId, newParent),
                    async () => {
                        if (childAndId) {
                            return mapErrorsOr(
                                await config.childStore.store(childAndId.childId, childAndId.child),
                                () => ({ parent: newParent, childAndId }),
                            );
                        } else {
                            return { value: { parent: newParent } };
                        }
                    },
                );
            }),
    };
}
